import { Dispatch, FC, SetStateAction } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { useUser } from '@mopla/business-logic';
import {
	CalendarIconV2,
	Checkbox,
	CheckboxWithLink,
	EMlpIconTypes,
	EMplVariantTypes,
	MplIconMessage,
} from '@mopla/ui';

import { PaymentDetails } from '../../organisms/PaymentDetails/PaymentDetails';

interface IStep3SummaryContentProps {
	startDate: string;
	isAcceptTariffs: boolean;
	setIsAcceptTariffs: Dispatch<SetStateAction<boolean>>;
	isAcceptEarlyServiceStart: boolean;
	setIsAcceptEarlyServiceStart: Dispatch<SetStateAction<boolean>>;
	isPriceZero: boolean;
}

export const Step3SummaryContent: FC<IStep3SummaryContentProps> = ({
	startDate,
	isAcceptTariffs,
	setIsAcceptTariffs,
	isAcceptEarlyServiceStart,
	setIsAcceptEarlyServiceStart,
	isPriceZero,
}) => {
	const { t } = useTranslation('subscribeDTicket');
	const { userData } = useUser();
	const toggleTariffsCheckbox = () => setIsAcceptTariffs((prev) => !prev);
	const toggleEarlyServiceStartCheckbox = () =>
		setIsAcceptEarlyServiceStart((prev) => !prev);

	const formattedDate = dayjs(startDate).format('01. MMMM YYYY');

	return (
		<Stack gap={5}>
			<Stack gap={1.5}>
				<Typography variant="h4-sb" color="primary.main">
					{t('subscribeDTicket:step3.header_start_date')}
				</Typography>

				<Stack direction="row" alignItems="center" gap={1}>
					<Box
						sx={{
							color: 'primary.main',
							display: 'flex',
							alignItems: 'center',
						}}
					>
						<CalendarIconV2 />
					</Box>
					<Typography
						variant="b1"
						color="primary.main"
						data-testid="subscribeDTicket-summary-startDate"
					>
						<Trans
							i18nKey="subscribeDTicket:step3.valid_from"
							values={{ startDate: formattedDate }}
							components={{
								bold: <strong />,
							}}
						/>
					</Typography>
				</Stack>

				<Box
					sx={{
						'& .MuiTypography-root': {
							fontSize: '12px',
							lineHeight: 1.5,
						},
					}}
				>
					<MplIconMessage
						type={EMlpIconTypes.Info}
						variant={EMplVariantTypes.Default}
						message={t('subscribeDTicket:step3.info_validation')}
					/>
				</Box>
			</Stack>

			<Stack gap={1}>
				<Typography variant="h4-sb" color="primary.main">
					{t('subscribeDTicket:step3.header_subscriber')}
				</Typography>

				<Stack gap={0.5}>
					<Typography
						variant="b2"
						color="primary.main"
						data-testid="subscribeDTicket-summary-holderName"
					>
						{userData?.firstName} {userData?.lastName}
					</Typography>
					{userData?.address && (
						<>
							<Typography variant="b2" color="primary.main">
								{userData.address.street} {userData.address.streetNumber}
							</Typography>
							<Typography variant="b2" color="primary.main">
								{userData.address.zipCode} {userData.address.city}
							</Typography>
						</>
					)}
				</Stack>
			</Stack>

			{!isPriceZero && (
				<Stack gap={1.5}>
					<Typography variant="h4-sb" color="primary.main">
						{t('subscribeDTicket:step3.header_payment')}
					</Typography>

					<PaymentDetails />

					<Box
						sx={{
							'& .MuiTypography-root': {
								fontSize: '12px',
								lineHeight: 1.5,
							},
						}}
					>
						<MplIconMessage
							type={EMlpIconTypes.Info}
							variant={EMplVariantTypes.Default}
							message={t('subscribeDTicket:step3.info_payment')}
						/>
					</Box>
				</Stack>
			)}

			<Stack gap={2}>
				<CheckboxWithLink
					url="https://www.mopla.solutions/de/tarifbestimmung-und-beforderungsbedingungen"
					linkText="subscribeDTicket:step3.accept_tariffs"
					checked={isAcceptTariffs}
					toggleCheckbox={toggleTariffsCheckbox}
					testId="dticket-acceptTariffs-checkbox"
				/>
				<Checkbox
					name="accept_early_service_start"
					label={t('subscribeDTicket:step3.accept_early_service_start')}
					checked={isAcceptEarlyServiceStart}
					onChange={toggleEarlyServiceStartCheckbox}
					checkboxTestId="dticket-acceptEarlyServiceStart-checkbox"
				/>
			</Stack>
		</Stack>
	);
};
